import { useEffect, useRef } from "react";

const useSSE = (vehicleIds, baseURL, onMessage) => {
  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const isConnectedRef = useRef(false);
  const lastVehicleIdsRef = useRef(vehicleIds.join(","));

  useEffect(() => {
    const currentVehicleIds = vehicleIds.join(",");
    if (currentVehicleIds === lastVehicleIdsRef.current && eventSourceRef.current) {
      console.log("Vehicle IDs unchanged, reusing existing connection");
      return; // Reuse existing connection if vehicleIds haven't changed
    }

    console.log("Vehicle IDs changed or initial setup, managing SSE connection");

    // Cleanup existing connection if it exists and vehicleIds have changed
    if (eventSourceRef.current) {
      console.log("Closing previous SSE connection due to vehicle ID change");
      eventSourceRef.current.close();
      eventSourceRef.current = null;
      isConnectedRef.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    }

    if (vehicleIds.length === 0) {
      console.log("No vehicle IDs, skipping SSE connection");
      return;
    }

    const connectSSE = () => {
      if (isConnectedRef.current) {
        console.log("Already connected, skipping new connection");
        return;
      }

      const query = vehicleIds.map((id) => `vehicleId=${id}`).join("&");
      const sseUrl = `${baseURL}/redis/stream?${query}`;
      console.log(`Attempting to connect to SSE URL: ${sseUrl}`);

      const source = new EventSource(sseUrl);
      eventSourceRef.current = source;

      source.onopen = () => {
        console.log("SSE connection opened successfully");
        isConnectedRef.current = true;
        lastVehicleIdsRef.current = currentVehicleIds;
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
      };

      source.onerror = (error) => {
        console.error("SSE connection error:", error);
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }
        isConnectedRef.current = false;
        if (!reconnectTimeoutRef.current) {
          console.log("Scheduling reconnection in 5 seconds");
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log("Reconnecting SSE...");
            connectSSE();
          }, 5000);
        }
      };

      source.onmessage = (event) => {
        try {
          console.log("Received SSE message:");
          const data = JSON.parse(event.data);
          onMessage(data);
        } catch (e) {
          console.error("Error parsing SSE message:", e);
        }
      };
    };

    connectSSE();

    return () => {
      console.log("Component unmounting, cleaning up SSE");
      if (eventSourceRef.current) {
        console.log("Closing SSE connection");
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        console.log("Clearing reconnect timeout");
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      isConnectedRef.current = false;
    };
  }, [vehicleIds.join(","), baseURL, onMessage]); // Depend on joined string for stability

  return eventSourceRef; // For debugging or manual control
};

export default useSSE;